import React, { Component, createRef } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { parseNginxIndex } from "../../../utils/parseNginxIndex";
import "./configGeneratorCard.css";

/* ── Sabitler ────────────────────────────────────────────────── */
const CACHE_TTL    = 10 * 60 * 1000;   // dizin listesi cache (10 dk)
const INDEX_TTL    = 30 * 60 * 1000;   // iso-paths.json sessionStorage TTL
const REQ_TIMEOUT  = 10000;            // tek autoindex isteği timeout (10 sn)
const ROOT_PATH    = '/mirrors/';
const INDEX_URL    = '/iso-paths.json';// cron'un yazdığı tüm-ISO yol listesi
const ISO_EXT_RE   = /\.(iso|img)$/i;

/* ── Dizin listesi cache ─────────────────────────────────────── */
const memCache = new Map();

function readCache(path) {
  if (memCache.has(path)) {
    const e = memCache.get(path);
    if (Date.now() - e.ts < CACHE_TTL) return e.items;
    memCache.delete(path);
  }
  try {
    const raw = sessionStorage.getItem('iso:' + path);
    if (!raw) return null;
    const e = JSON.parse(raw);
    if (Date.now() - e.ts >= CACHE_TTL) {
      sessionStorage.removeItem('iso:' + path);
      return null;
    }
    memCache.set(path, e);
    return e.items;
  } catch { return null; }
}

function writeCache(path, items) {
  const entry = { ts: Date.now(), items };
  memCache.set(path, entry);
  try { sessionStorage.setItem('iso:' + path, JSON.stringify(entry)); }
  catch { /* dolu olabilir */ }
}

/* ── Tek dizin için canlı autoindex çek (cache'lenir) ────────── */
async function fetchListing(path) {
  const cached = readCache(path);
  if (cached) return cached;
  const { data } = await axios.get(path, { responseType: 'text', timeout: REQ_TIMEOUT });
  const parsed = parseNginxIndex(data);
  writeCache(path, parsed);
  return parsed;
}

/* ── Sunucudan gelen ISO path indexi (cron çıktısı) ──────────── */
let isoIndex        = null;            // { isoPaths: [...] }
let isoIndexPromise = null;             // tek-uçuşlu yükleme

function readStoredIndex() {
  try {
    const raw = sessionStorage.getItem('isoIdx');
    if (!raw) return null;
    const e = JSON.parse(raw);
    if (Date.now() - e.ts >= INDEX_TTL) {
      sessionStorage.removeItem('isoIdx');
      return null;
    }
    return e.data;
  } catch { return null; }
}

function writeStoredIndex(data) {
  try { sessionStorage.setItem('isoIdx', JSON.stringify({ ts: Date.now(), data })); }
  catch { /* dolu olabilir */ }
}

async function loadIsoIndex() {
  if (isoIndex) return isoIndex;
  if (isoIndexPromise) return isoIndexPromise;

  const stored = readStoredIndex();
  if (stored) { isoIndex = stored; return stored; }

  isoIndexPromise = axios.get(INDEX_URL, { timeout: 5000 })
    .then(r => {
      const data = (r.data && Array.isArray(r.data.isoPaths))
        ? { isoPaths: r.data.isoPaths }
        : { isoPaths: [] };
      isoIndex = data;
      if (data.isoPaths.length > 0) writeStoredIndex(data);
      return data;
    })
    .catch(() => {
      const empty = { isoPaths: [] };
      isoIndex = empty;
      return empty;
    });
  return isoIndexPromise;
}

/* ── subtreeHasIso ─────────────────────────────────────────────
 * Sadece JSON index ile prefix-match (cron günlük yeniler).
 * Index yok / boşsa "true" dön — yani filtreleme yapma, hepsi görünsün.
 */
async function subtreeHasIso(path) {
  const idx = await loadIsoIndex();
  if (!idx || !idx.isoPaths || idx.isoPaths.length === 0) return true;
  return idx.isoPaths.some(p => p.startsWith(path));
}

/* ── Canlı Tree FileBrowser ──────────────────────────────────── */
/**
 * Tamamen canlı çalışır:
 *  • Modal açılınca /mirrors/ kökü taranır
 *  • Her klasöre tıklamak yeni sütun açar
 *  • İçinde .iso/.img barındıran dizine gelince dosyalar listelenir
 */
class FileBrowser extends Component {
  rootRef = createRef();

  /**
   * columns: [{ path: '/mirrors/', items, loading, error, files }, ...]
   *   path  : sütunun bağlı olduğu mutlak dizin yolu
   *   items : dizinler (children için)
   *   files : dosyalar (.iso/.img — leaf seçimi için)
   *   activeChild: tıklanan child path (highlight)
   */
  state = {
    columns: [{ path: ROOT_PATH, items: null, files: null, loading: true, error: null, activeChild: null }],
  };

  componentDidMount() {
    this.loadColumn(0, ROOT_PATH);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.columns.length !== this.state.columns.length) {
      const el = this.rootRef.current;
      if (el) {
        requestAnimationFrame(() => {
          el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
        });
      }
    }
  }

  /** Belirli bir sütundaki dizini fetch et + JSON ile alt dizinleri filtrele */
  loadColumn = async (index, path) => {
    // Loading state
    this.setState(prev => {
      const cols = prev.columns.slice(0, index + 1);
      cols[index] = { path, items: null, files: null, loading: true, error: null, activeChild: null, probing: false };
      return { columns: cols };
    });

    let parsed;
    try {
      parsed = await fetchListing(path);
    } catch {
      this.setState(prev => {
        const cols = prev.columns.slice();
        cols[index] = { path, items: [], files: [], loading: false, error: 'Failed to load.', activeChild: null, probing: false };
        return { columns: cols };
      });
      return;
    }

    const allDirs = parsed.filter(p => p.isDir);
    const files   = parsed.filter(p => !p.isDir && ISO_EXT_RE.test(p.name));

    if (allDirs.length === 0) {
      this.setState(prev => {
        if (!prev.columns[index] || prev.columns[index].path !== path) return null;
        const cols = prev.columns.slice(0, index + 1);
        cols[index] = { path, items: [], files, loading: false, error: null, activeChild: null, probing: false };
        return { columns: cols };
      });
      return;
    }

    // Probing state — boş liste + spinner ile başla, her bulunanı anında ekle
    this.setState(prev => {
      if (!prev.columns[index] || prev.columns[index].path !== path) return null;
      const cols = prev.columns.slice(0, index + 1);
      cols[index] = { path, items: [], files, loading: false, error: null, activeChild: null, probing: true };
      return { columns: cols };
    });

    // Mirror sırasını korumak için index'le birlikte probe
    let pending = allDirs.length;
    allDirs.forEach((d, i) => {
      subtreeHasIso(path + d.href)
        .then(has => {
          if (has) {
            this.setState(prev => {
              if (!prev.columns[index] || prev.columns[index].path !== path) return null;
              const cols = prev.columns.slice();
              const cur = cols[index];
              const merged = [...(cur.items || []), { ...d, _ord: i }]
                .sort((a, b) => a._ord - b._ord);
              cols[index] = { ...cur, items: merged };
              return { columns: cols };
            });
          }
        })
        .catch(() => { /* ignore */ })
        .finally(() => {
          pending--;
          if (pending === 0) {
            this.setState(prev => {
              if (!prev.columns[index] || prev.columns[index].path !== path) return null;
              const cols = prev.columns.slice();
              cols[index] = { ...cols[index], probing: false };
              return { columns: cols };
            });
          }
        });
    });
  };

  /** Klasör sütunundan bir öğeye tıklandı — alt dizinleri için fresh live probe */
  handleDirClick = (colIndex, dir) => {
    const col = this.state.columns[colIndex];
    const newPath = col.path + dir.href;

    // Aktif vurguyu güncelle ve sonraki sütunları temizle
    this.setState(prev => {
      const cols = prev.columns.slice(0, colIndex + 1);
      cols[colIndex] = { ...cols[colIndex], activeChild: dir.href };
      cols.push({ path: newPath, items: null, files: null, loading: true, error: null, activeChild: null });
      return { columns: cols };
    }, () => {
      this.loadColumn(colIndex + 1, newPath);
    });

    // Önceki dosya seçimini temizle
    if (this.props.onSelect) this.props.onSelect(null);
  };

  /** Dosya tıklandı (leaf) */
  handleFileClick = (colIndex, file) => {
    const col = this.state.columns[colIndex];
    const fullUrl = col.path + file.href;
    this.setState(prev => {
      const cols = prev.columns.slice();
      cols[colIndex] = { ...cols[colIndex], activeChild: file.href };
      return { columns: cols };
    });
    if (this.props.onSelect) this.props.onSelect(fullUrl);
  };

  render() {
    const { columns } = this.state;

    return (
      <div className="fb-root" ref={this.rootRef}>
        {columns.map((col, idx) => {
          const isFileColumn = col.files && col.files.length > 0;

          return (
            <ul
              key={idx + ':' + col.path}
              className={`fb-column${isFileColumn ? ' fb-column--live' : ''}`}
            >
              {/* Yükleniyor */}
              {col.loading && (
                <li className="fb-item fb-item--header">
                  <span className="fb-item__label">Loading…</span>
                  <LoadingOutlined style={{ opacity: 0.7 }} />
                </li>
              )}

              {/* JSON ile filtreleme (anlık) */}
              {col.probing && !col.loading && (
                <li className="fb-item fb-item--header">
                  <span className="fb-item__label">Filtering…</span>
                  <LoadingOutlined style={{ opacity: 0.7 }} />
                </li>
              )}

              {/* Hata */}
              {col.error && !col.loading && (
                <li className="fb-item fb-item--error" title={col.error}>
                  <span className="fb-item__label">{col.error}</span>
                </li>
              )}

              {/* Boş — ISO yok */}
              {!col.loading && !col.error && !col.probing &&
                (!col.items || col.items.length === 0) &&
                (!col.files || col.files.length === 0) && (
                  <li className="fb-item fb-item--leaf" style={{ opacity: 0.55 }}>
                    <span className="fb-item__label">No ISO files here</span>
                  </li>
                )}

              {/* Dosya sütunu başlığı */}
              {isFileColumn && (
                <li className="fb-item fb-item--header">
                  <span className="fb-item__label">ISO files</span>
                </li>
              )}

              {/* Klasörler */}
              {!col.loading && !col.error && col.items && col.items.map(dir => {
                const isActive = col.activeChild === dir.href;
                return (
                  <li
                    key={'d-' + dir.href}
                    className={`fb-item${isActive ? ' fb-item--active' : ''}`}
                    onClick={() => this.handleDirClick(idx, dir)}
                    title={dir.name}
                  >
                    <span className="fb-item__label">{dir.name}</span>
                    <svg className="fb-item__arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </li>
                );
              })}

              {/* Dosyalar */}
              {!col.loading && !col.error && col.files && col.files.map(file => {
                const isActive = col.activeChild === file.href;
                return (
                  <li
                    key={'f-' + file.href}
                    className={`fb-item fb-item--leaf${isActive ? ' fb-item--active' : ''}`}
                    onClick={() => this.handleFileClick(idx, file)}
                    title={file.name}
                  >
                    <span className="fb-item__label">{file.name}</span>
                    {file.size && <span className="fb-item__size">{file.size}</span>}
                  </li>
                );
              })}
            </ul>
          );
        })}
      </div>
    );
  }
}

/* ── Modal sarıcısı ──────────────────────────────────────────── */
export default class DownloadFormCard extends Component {
  state = {
    downloadFormVisible: false,
    selectDistrib: undefined
  };

  showDownloadForm = () => this.setState({ downloadFormVisible: true });
  handleCancel     = () => this.setState({ downloadFormVisible: false, selectDistrib: undefined });

  onSelect = value => {
    this.setState({
      selectDistrib: value && value.startsWith && (value.startsWith('http') || value.startsWith('/')) ? value : undefined
    });
  };

  render() {
    const { downloadFormVisible, selectDistrib } = this.state;

    const modal = downloadFormVisible
      ? ReactDOM.createPortal(
          <div className="fb-modal-backdrop" onClick={this.handleCancel}>
            <div
              className="fb-modal"
              onClick={e => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Choose a release"
            >
              <div className="fb-modal__head">
                <h2 className="fb-modal__title">Choose a release</h2>
                <button className="fb-modal__close" onClick={this.handleCancel} aria-label="Kapat">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              <div className="fb-modal__body">
                <FileBrowser onSelect={this.onSelect} />
              </div>

              <div className="fb-modal__foot">
                <span className="fb-modal__hint">
                  {selectDistrib
                    ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Ready to download
                      </>
                    )
                    : 'Navigate the tree and select an ISO file'}
                </span>
                <a
                  className={`cta${selectDistrib ? '' : ' cta--disabled'}`}
                  href={selectDistrib || undefined}
                  download
                  onClick={e => !selectDistrib && e.preventDefault()}
                >
                  <DownloadOutlined style={{ marginRight: 6 }} />
                  Download
                </a>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

    return (
      <>
        <button className="cta" onClick={this.showDownloadForm}>
          <DownloadOutlined style={{ marginRight: 8 }} />
          Download ISO
        </button>
        {modal}
      </>
    );
  }
}
